import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { PencilIcon, CheckIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';

/**
 * ConcessionTypesModal - manage the concession-type registry.
 * Create new class purses and rename existing ones. 'shared' is implicit
 * and shown read-only. Frontend-only convenience over
 * GET/POST /api/admin/concession-types + PUT /:key.
 */
const ConcessionTypesModal = ({ isOpen, onClose, onChanged }) => {
  const [types, setTypes] = useState([]);
  const [newName, setNewName] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [busy, setBusy] = useState(false);

  const loadTypes = () => {
    axios.get('/api/admin/concession-types')
      .then((res) => setTypes(res.data?.types || []))
      .catch(() => toast.error('Failed to load concession types'));
  };

  useEffect(() => {
    if (isOpen) {
      loadTypes();
      setNewName('');
      setEditingKey(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Enter a display name (e.g. "Yoga")');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post('/api/admin/concession-types', { displayName: newName.trim() });
      toast.success(res.data.message || 'Created');
      setNewName('');
      loadTypes();
      onChanged && onChanged();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create type');
    } finally {
      setBusy(false);
    }
  };

  const startRename = (t) => {
    setEditingKey(t.key);
    setEditingName(t.displayName);
  };

  const handleDelete = async (t) => {
    const name = t.displayName;
    const ok = window.confirm(
      `Remove concession type "${name}"?\n\nThis can only be done once it is no longer used by any class or user. Any historical records are kept.`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await axios.delete(`/api/admin/concession-types/${t.key}`);
      toast.success(res.data.message || 'Removed');
      loadTypes();
      onChanged && onChanged();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove type');
    } finally {
      setBusy(false);
    }
  };

  const saveRename = async () => {
    if (!editingName.trim()) {
      toast.error('Display name cannot be empty');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.put(`/api/admin/concession-types/${editingKey}`, {
        displayName: editingName.trim()
      });
      toast.success(res.data.message || 'Renamed');
      setEditingKey(null);
      loadTypes();
      onChanged && onChanged();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to rename type');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="concessions-fullscreen-overlay" onClick={busy ? undefined : onClose}>
      <div className="concessions-fullscreen-container" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-primary">
          <h3 className="text-lg font-semibold text-primary">Concessions</h3>
          <button onClick={onClose} className="p-1 text-secondary hover:text-primary" disabled={busy}>
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4">
          <p className="text-sm text-secondary mb-3">
            Each type is a separate concession wallet. Members marked with a type can see and book
            classes of that type.
          </p>

          {/* Existing types */}
          <div className="border border-primary rounded-lg divide-y divide-primary mb-4">
            {types.filter(t => !t.isShared).map((t) => (
              <div key={t.key} className="flex items-center justify-between px-3 py-2">
                {editingKey === t.key ? (
                  <>
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && saveRename()}
                      className="form-input flex-1 mr-2 px-2 py-1 rounded-lg"
                      disabled={busy}
                      autoFocus
                    />
                    <button
                      onClick={saveRename}
                      className="flex items-center px-2 py-1 rounded-lg text-success-600 hover:text-success-700 bg-success-50 hover:bg-success-100 mr-2 text-sm font-medium"
                      disabled={busy}
                    >
                      <CheckIcon className="w-4 h-4 mr-1" />
                      Save
                    </button>
                    <button
                      onClick={() => setEditingKey(null)}
                      className="flex items-center px-2 py-1 rounded-lg text-secondary-700 hover:text-secondary-900 bg-secondary-100 hover:bg-secondary-200 text-sm font-medium"
                      disabled={busy}
                    >
                      <XMarkIcon className="w-4 h-4 mr-1" />
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <div>
                      <span className="text-sm font-medium text-primary">{t.displayName}</span>
                    </div>
                    <div className="flex items-center">
                      <button
                        onClick={() => startRename(t)}
                        className="p-1 text-secondary hover:text-primary"
                        disabled={busy}
                        title="Rename"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(t)}
                        className="p-1 text-red-600 hover:text-red-700"
                        disabled={busy}
                        title="Remove"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
            {types.length === 0 && (
              <div className="px-3 py-2 text-sm text-secondary">Loading…</div>
            )}
          </div>

          {/* Create new */}
          <label className="form-label-small text-secondary">Add new type</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder='e.g. "Yoga" becomes wallet "yoga"'
              className="form-input flex-1 px-3 py-2 rounded-lg"
              disabled={busy}
            />
            <button
              onClick={handleCreate}
              className="btn-primary btn-center-content whitespace-nowrap"
              disabled={busy}
            >
              {busy ? 'Saving…' : 'Add'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConcessionTypesModal;
